import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { DollarSign, Edit, Trash2 } from 'lucide-react';
import { formatNumber, PAYMENT_METHODS } from '../../utils/constants';

const OrderPaymentDialog = ({
  open,
  onOpenChange,
  paymentOrder,
  orderPayments,
  paymentForm,
  setPaymentForm,
  editingPayment,
  onRecordPayment,
  onEditPayment,
  onUpdatePayment,
  onDeletePayment,
  onCancelEdit
}) => {
  return (
    <Dialog open={open} onOpenChange={(o) => {
      onOpenChange(o);
      if (!o) {
        onCancelEdit();
      }
    }}>
      <DialogContent className="max-w-md" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle>{editingPayment ? 'Edit Payment' : 'Record Payment'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Order Info */}
          <div className="bg-muted/30 p-4 rounded-lg space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Order #:</span>
              <span className="font-mono">{paymentOrder?.order_number || paymentOrder?.id?.substring(0, 8).toUpperCase()}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Customer:</span>
              <span className="font-medium">{paymentOrder?.customer_name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Order Total:</span>
              <span className="font-semibold">RWF {formatNumber(paymentOrder?.total)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Amount Paid:</span>
              <span className="text-emerald-600 font-medium">RWF {formatNumber(orderPayments.total_paid)}</span>
            </div>
            <div className="flex justify-between text-sm border-t pt-2 mt-2">
              <span className="font-medium">Balance Due:</span>
              <span className="text-amber-600 font-bold">RWF {formatNumber(orderPayments.balance_due)}</span>
            </div>
          </div>

          {/* Payment History */}
          {orderPayments.payments?.length > 0 && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">Payment History</label>
              <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                {orderPayments.payments.map((payment) => (
                  <div key={payment.id} className="flex justify-between items-center text-xs bg-muted/20 p-2 rounded">
                    <div>
                      <span>{new Date(payment.date || payment.created_at).toLocaleDateString()}</span>
                      <span className="mx-2">-</span>
                      <span>{payment.payment_method}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">RWF {formatNumber(payment.amount)}</span>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => onEditPayment(payment)}>
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => onDeletePayment(payment.id)}>
                        <Trash2 className="w-3 h-3 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payment Form */}
          {(orderPayments.balance_due > 0 || editingPayment) && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">
                  {editingPayment ? 'Edit Payment Amount (RWF)' : 'Payment Amount (RWF)'}
                </label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder={editingPayment ? 'Enter amount' : `Max: ${formatNumber(orderPayments.balance_due)}`}
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  data-testid="payment-amount-input"
                />
                {!editingPayment && orderPayments.balance_due > 0 && (
                  <div className="flex gap-2 mt-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setPaymentForm({ ...paymentForm, amount: orderPayments.balance_due.toString() })}
                    >
                      Pay Full Balance
                    </Button>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Payment Method</label>
                <Select
                  value={paymentForm.payment_method}
                  onValueChange={(value) => setPaymentForm({ ...paymentForm, payment_method: value })}
                >
                  <SelectTrigger data-testid="payment-method-input">
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map(method => (
                      <SelectItem key={method} value={method}>{method}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Payment Date</label>
                <Input
                  type="date"
                  value={paymentForm.payment_date || new Date().toISOString().split('T')[0]}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                  data-testid="payment-date-input"
                />
              </div>

              {editingPayment ? (
                <div className="flex gap-2">
                  <Button variant="outline" onClick={onCancelEdit} className="flex-1">
                    Cancel
                  </Button>
                  <Button onClick={onUpdatePayment} className="flex-1" data-testid="update-payment-btn">
                    <Edit className="w-4 h-4 mr-2" />
                    Update Payment
                  </Button>
                </div>
              ) : (
                <Button onClick={onRecordPayment} className="w-full" data-testid="submit-payment-btn">
                  <DollarSign className="w-4 h-4 mr-2" />
                  Record Payment
                </Button>
              )}
            </>
          )}

          {orderPayments.balance_due <= 0 && !editingPayment && (
            <div className="text-center py-3 mb-3 bg-emerald-50 rounded-lg">
              <div className="text-emerald-600 font-semibold">Fully Paid</div>
              <p className="text-xs text-muted-foreground mt-1">Click on a payment above to edit it</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { OrderPaymentDialog };
